import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { socket } from '../lib/socket';

interface SocketContextValue {
  isConnected: boolean;
  hasConnected: boolean;
}

const SocketContext = createContext<SocketContextValue | null>(null);

export function SocketProvider({ children }: { children: ReactNode }) {
  const [isConnected, setIsConnected] = useState(socket.connected);
  const hasConnectedRef = useRef(socket.connected);
  const [hasConnected, setHasConnected] = useState(socket.connected);

  useEffect(() => {
    function onConnect() {
      setIsConnected(true);
      if (!hasConnectedRef.current) {
        hasConnectedRef.current = true;
        setHasConnected(true);
      }
    }
    function onDisconnect() {
      setIsConnected(false);
    }

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);

    // Tab backgrounding recovery — reconnect when tab becomes visible
    function onVisibilityChange() {
      if (document.visibilityState === 'visible' && !socket.connected && socket.active) {
        socket.connect();
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  return (
    <SocketContext.Provider value={{ isConnected, hasConnected }}>
      {children}
    </SocketContext.Provider>
  );
}

export function useSocket() {
  const ctx = useContext(SocketContext);
  if (!ctx) throw new Error('useSocket must be used within SocketProvider');
  return ctx;
}
